import React from 'react';
import { Database, HardDrive, Calendar, Download, Trash2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { sumBackupStats, formatDate } from './securityHelpers';

const StatCard = ({ label, value, Icon, iconColor }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          {typeof value === 'string' ? (
            <p className="text-sm font-medium">{value}</p>
          ) : (
            <p className="text-2xl font-bold">{value}</p>
          )}
        </div>
        <Icon className={`h-8 w-8 ${iconColor}`} />
      </div>
    </CardContent>
  </Card>
);

export const BackupsTab = ({
  backupStats,
  backups,
  backupsLoading,
  onRefresh,
  onCreate,
  onDownload,
  onDelete,
}) => (
  <div className="space-y-4">
    {/* Backup Stats */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <StatCard label="Total Backups" value={backupStats?.total_backups || 0} Icon={Database} iconColor="text-blue-500" />
      <StatCard label="Total Size" value={`${backupStats?.total_size_mb || 0} MB`} Icon={HardDrive} iconColor="text-green-500" />
      <StatCard
        label="Latest Backup"
        value={backupStats?.latest_backup ? formatDate(backupStats.latest_backup.created_at) : 'None'}
        Icon={Calendar}
        iconColor="text-amber-500"
      />
      <Card>
        <CardContent className="pt-6 flex items-center justify-center">
          <Button onClick={onCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="create-backup-btn">
            <Database className="h-4 w-4 mr-2" />
            Create Backup
          </Button>
        </CardContent>
      </Card>
    </div>

    {/* Backup List */}
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Backup History</CardTitle>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {backupsLoading ? (
          <div className="text-center py-8">Loading backups...</div>
        ) : backups.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No backups found. Create your first backup to protect your data.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Backup ID</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Records</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {backups.map((backup) => (
                <TableRow key={backup.backup_id}>
                  <TableCell className="font-mono text-sm">{backup.backup_id}</TableCell>
                  <TableCell>{formatDate(backup.created_at)}</TableCell>
                  <TableCell>{backup.created_by_email}</TableCell>
                  <TableCell>{backup.file_size_mb} MB</TableCell>
                  <TableCell>{sumBackupStats(backup.stats)}</TableCell>
                  <TableCell>
                    {backup.file_exists ? (
                      <Badge className="bg-green-100 text-green-800">Available</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800">Missing</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDownload(backup.backup_id)}
                        disabled={!backup.file_exists}
                        data-testid={`download-backup-${backup.backup_id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(backup.backup_id)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`delete-backup-${backup.backup_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  </div>
);
